<script setup lang="ts">
import { computed } from 'vue'

import type {
  MetaDraftErrors,
  MetaDraftValue,
  MetaWatchlistDraft,
  WatchlistFormField,
} from '../../models/watchlist.types'

import MetaFilterField from './MetaFilterField.vue'

import { useI18n } from '@/shared/i18n'
import { Button, Icon, Input } from '@/shared/ui'

const props = defineProps<{
  draft: MetaWatchlistDraft
  fields: WatchlistFormField[]
  expanded: boolean
  errors: MetaDraftErrors
  submitted: boolean
}>()
const emit = defineEmits<{
  toggle: []
  remove: []
  patch: [key: string, value: MetaDraftValue]
  touch: [field: 'config_name' | 'page_id']
}>()
const { t } = useI18n()
const name = computed(() => String(props.draft.values.config_name ?? ''))
const pageId = computed(() =>
  Array.isArray(props.draft.values.search_page_ids)
    ? String(props.draft.values.search_page_ids[0] ?? '')
    : '',
)
const initials = computed(
  () =>
    name.value
      .trim()
      .split(/\s+/u)
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase() || '?',
)
const visibleNameError = computed(() =>
  props.submitted || props.draft.touched.config_name ? props.errors.config_name : undefined,
)
const visiblePageError = computed(() =>
  props.submitted || props.draft.touched.page_id ? props.errors.page_id : undefined,
)
const activeFilterCount = computed(
  () =>
    props.fields.filter((field) => {
      const value = props.draft.values[field.key]
      return Array.isArray(value)
        ? value.length > 0
        : Boolean(value && (typeof value !== 'object' || value.start || value.end))
    }).length,
)
const errorKey = (code: string | undefined): string | undefined =>
  code ? `workspaceDefinitions.watchlist.meta.error.${code}` : undefined
</script>

<template>
  <article
    class="meta-card overflow-hidden border border-border rounded-md bg-surface"
    :class="{ 'meta-card--invalid': visibleNameError || visiblePageError }"
    :data-testid="`meta-config-${draft.uid}`"
  >
    <div class="meta-card__main flex items-start gap-sm p-lg">
      <span
        class="meta-card__avatar grid flex-none w-8 h-8 place-items-center mt-2xs rounded-full bg-primary text-primary-fg text-[0.75rem] font-bold"
        >{{ initials }}</span
      >
      <div class="meta-card__identity grid flex-1 grid-cols-2 gap-md max-md:grid-cols-[1fr]">
        <Input
          :model-value="name"
          :label="t('workspaceDefinitions.watchlist.meta.fields.brandName')"
          :placeholder="t('workspaceDefinitions.watchlist.meta.fields.brandNamePlaceholder')"
          size="sm"
          :error="Boolean(visibleNameError)"
          v-bind="visibleNameError ? { errorMessage: t(errorKey(visibleNameError)!) } : {}"
          @update:model-value="emit('patch', 'config_name', String($event))"
          @blur="emit('touch', 'config_name')"
        />
        <Input
          :model-value="pageId"
          :label="t('workspaceDefinitions.watchlist.meta.fields.pageId')"
          :placeholder="t('workspaceDefinitions.watchlist.meta.fields.pageIdPlaceholder')"
          size="sm"
          :error="Boolean(visiblePageError)"
          v-bind="visiblePageError ? { errorMessage: t(errorKey(visiblePageError)!) } : {}"
          @update:model-value="emit('patch', 'search_page_ids', [String($event)])"
          @blur="emit('touch', 'page_id')"
        />
      </div>
      <Button
        variant="soft"
        size="sm"
        square
        :aria-label="t('workspaceDefinitions.watchlist.meta.action.remove')"
        data-testid="meta-remove-config"
        @click="emit('remove')"
      >
        <Icon name="x" :size="14" />
      </Button>
    </div>
    <button
      type="button"
      class="meta-card__summary flex items-center gap-xs pt-0 pr-lg pb-md pl-14 text-muted text-[0.75rem]"
      @click="emit('toggle')"
    >
      <Icon name="chevron-down" :size="13" :class="{ 'meta-card__chevron--open': expanded }" />
      {{ t('workspaceDefinitions.watchlist.meta.filters', { count: activeFilterCount }) }}
    </button>
    <div
      v-if="expanded"
      class="meta-card__filters grid grid-cols-3 gap-md p-lg bg-surface-sunken max-md:grid-cols-[1fr]"
    >
      <MetaFilterField
        v-for="field in fields"
        :key="field.key"
        :field="field"
        :value="draft.values[field.key] ?? null"
        @change="emit('patch', field.key, $event)"
      />
    </div>
  </article>
</template>

<style scoped>
.meta-card.meta-card--invalid {
  border-color: var(--ui-color-warning);
}
.meta-card .meta-card__chevron--open {
  transform: rotate(180deg);
}
.meta-card .meta-card__filters :deep(.ui-chip-input) {
  grid-column: 1 / -1;
}
</style>
